import { Button } from '@/components/ui/button';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import { useUpdateMemberActivation } from 'components/IncidentConfig/hooks/useUpdateMemberActivation';
import IncidentNonActiveConfirmDialog from 'components/IncidentConfig/IncidentNonActiveConfirmDialog';
import { SeatActivationRequestControl } from 'components/IncidentConfig/roster/SeatActivationRequestControl';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { getInitials } from 'helpers/intials';
import {
  useAddUsersToIncidentRoster,
  useMarkIncidentUserActive,
  useRemoveMemberFromPositionInRoster,
} from 'hooks/incident/useRoster';
import {
  useCreateIncidentMemberSession,
  useGetLatestIncidentWorkLogForUser,
  useUpdateIncidentMemberSession,
  WORK_LOG_ACTIONS,
} from 'hooks/incident/useWorkLog';
import React from 'react';
import { useCallback, useMemo } from 'react';

const PositionAssignee: React.FC<{
  incidentId: string;
  groupId: string;
  positionId: string;
  positionName: string;
  positionMember: RosterEntry;
  isDraft?: boolean;
  /** Set when this seat is on a planned operational period's roster. */
  plannedPeriodId?: string | null;
  draftExists: boolean;
  alreadyOnDraftForPosition: boolean;
}> = ({
  incidentId,
  groupId,
  positionId,
  positionName,
  positionMember,
  isDraft = false,
  plannedPeriodId,
  draftExists,
  alreadyOnDraftForPosition,
}) => {
  const isCreateMode = !!useOptionalCreateRosterDraft();
  const { data: latestIncidentWorkLog } = useGetLatestIncidentWorkLogForUser(
    incidentId,
    positionMember.userId,
    { enabled: !isCreateMode && !!incidentId }
  );
  const createSession = useCreateIncidentMemberSession();
  const updateSession = useUpdateIncidentMemberSession();
  const checkInMember = useMarkIncidentUserActive(incidentId, groupId);
  const removeMember = useRemoveMemberFromPositionInRoster(incidentId, groupId);
  const updateMemberActivation = useUpdateMemberActivation();
  const addUserToDraft = useAddUsersToIncidentRoster(incidentId, groupId);

  const [showNonActiveMemberDialog, setShowNonActiveMemberDialog] =
    React.useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    React.useState(false);

  // Show the → Draft button only when a draft roster exists and we are on the Active side
  const showScheduleForNextOp = draftExists && !isDraft && !plannedPeriodId;

  const workLogId = React.useMemo(() => {
    return latestIncidentWorkLog?.workLogId || null;
  }, [latestIncidentWorkLog]);

  const latestSession = useMemo(() => {
    if (!latestIncidentWorkLog) {
      return null;
    }
    // useGetLatestIncidentWorkLogForUser returns only one session, the most recent
    return latestIncidentWorkLog.sessions?.[0] || null;
  }, [latestIncidentWorkLog]);

  const availableActions = useMemo(() => {
    return latestIncidentWorkLog?.availableActions || [];
  }, [latestIncidentWorkLog]);

  const handleCheckIn = useCallback(() => {
    if (positionMember.active && positionMember.activeTime) {
      return;
    } else {
      checkInMember.mutate({
        user_guid: positionMember.userId,
        incident_member_id: positionMember.incidentMemberId,
      });
    }
  }, [checkInMember, positionMember]);

  const handleDemobilize = useCallback(() => {
    if (!positionMember.active || !positionMember.activeTime) {
      return;
    } else {
      setShowNonActiveMemberDialog(true);
    }
  }, [positionMember]);

  const handleSignIn = useCallback(() => {
    if (!workLogId) {
      return;
    }
    createSession.mutate({
      incidentId,
      userGuid: positionMember.userId,
      workLogId,
      signInTime: new Date().toISOString(),
    });
  }, [createSession, incidentId, positionMember.userId, workLogId]);

  const handleSignOut = useCallback(() => {
    if (!workLogId || !latestSession) {
      return;
    }
    updateSession.mutate({
      incidentId,
      userGuid: positionMember.userId,
      workLogId,
      sessionId: latestSession?.sessionId || '',
      signInTime: latestSession?.signInTime || '',
      signOutTime: new Date().toISOString(),
    });
  }, [
    updateSession,
    incidentId,
    positionMember.userId,
    workLogId,
    latestSession,
  ]);

  const handleActivationUpdate = useCallback(
    (isActivated: boolean) => {
      updateMemberActivation.mutate({
        /* eslint-disable camelcase */
        incident_id: incidentId,
        member_user_guid: positionMember.userId,
        is_activated: isActivated,
        decline_note: isActivated ? undefined : 'Declined activation',
        /* eslint-enable camelcase */
      });
    },
    [updateMemberActivation, incidentId, positionMember.userId]
  );

  const handleAddToDraft = useCallback(() => {
    addUserToDraft.mutate({
      positionAssignments: [
        {
          positionId,
          positionName,
          incidentMemberUserId: positionMember.userId,
          isLeader: false,
        },
      ],
      isDraft: true,
    });
  }, [addUserToDraft, positionId, positionName, positionMember.userId]);

  return (
    <div className="d-flex flex-column width-100">
      {/* Row 1: name/email left | → Draft + delete right */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-row">
        <div className="d-flex align-items-center flex-row">
          <div
            className="rounded-circle avatar-initials sml team-card__members__item__avatar"
            style={{ marginRight: '1rem' }}
          >
            {getInitials(
              positionMember.profileSettings?.userName ||
                positionMember.emailAddress ||
                ''
            )}
          </div>
          <div className="d-flex flex-column">
            <span>{positionMember.userName || '[No name recorded]'}</span>
            <span className="text-muted" style={{ fontSize: '0.8em' }}>
              {positionMember.emailAddress}
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center flex-row gap-2">
          {showScheduleForNextOp && !isCreateMode && (
            <Button
              type="button"
              size="sm"
              onClick={handleAddToDraft}
              disabled={alreadyOnDraftForPosition || addUserToDraft.isPending}
              title={
                alreadyOnDraftForPosition
                  ? 'Already scheduled for next OP'
                  : undefined
              }
            >
              → Draft
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setShowDeleteConfirmDialog(true)}
          >
            <SharedIcon
              iconName="delete"
              size="sm"
              color="light"
              title={'Remove user from position'}
              aria-label={'Remove user from position'}
            />
          </Button>
        </div>
      </div>

      {!isCreateMode && !isDraft ? (
        <SeatActivationRequestControl
          incidentId={incidentId}
          groupId={groupId}
          positionId={positionId}
          member={positionMember}
          plannedPeriodId={plannedPeriodId}
          caption="ACTIVATION REQUEST"
          className="tw:mb-2"
        />
      ) : null}

      {/* Row 2: status blocks left-aligned */}
      {!isCreateMode && !plannedPeriodId ? (
        <div className="d-flex flex-row gap-3">
          <div
            className="d-flex flex-column align-items-start"
            style={{ width: '7rem' }}
          >
            <span className="mb-1" style={{ fontSize: '0.8em' }}>
              ACTIVATION
            </span>
            <div className="d-flex flex-column">
              {positionMember.activatedTimestamp ? (
                <span className="text-success mb-2">Active</span>
              ) : positionMember.declinedTimestamp ? (
                <span className="text-danger mb-2">Declined</span>
              ) : (
                <span className="text-warning mb-2">Pending</span>
              )}
              {(availableActions.includes(WORK_LOG_ACTIONS.activate) ||
                availableActions.includes(WORK_LOG_ACTIONS.decline)) && (
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Button
                    type="button"
                    size="sm"
                    className="tw:w-full"
                    onClick={() => handleActivationUpdate(true)}
                    disabled={
                      !availableActions.includes(WORK_LOG_ACTIONS.activate)
                    }
                  >
                    Activate
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="tw:w-full"
                    onClick={() => handleActivationUpdate(false)}
                    disabled={
                      !availableActions.includes(WORK_LOG_ACTIONS.decline)
                    }
                  >
                    Decline
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex flex-column" style={{ width: '7rem' }}>
            <span className="mb-1" style={{ fontSize: '0.8em' }}>
              CHECK-IN
            </span>
            {positionMember.active && positionMember.activeTime ? (
              <div className="d-flex flex-column">
                <span className="text-success mb-2">Checked-In</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDemobilize}
                  disabled={
                    !availableActions.includes(WORK_LOG_ACTIONS.checkOut)
                  }
                >
                  Demobilize
                </Button>
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span className="text-warning mb-2">Demobilized</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCheckIn}
                  disabled={
                    !availableActions.includes(WORK_LOG_ACTIONS.checkIn)
                  }
                >
                  Check In
                </Button>
              </div>
            )}
          </div>
          <div className="d-flex flex-column" style={{ width: '7rem' }}>
            <span className="mb-1" style={{ fontSize: '0.8em' }}>
              SIGN-IN
            </span>
            {latestSession?.signInTime && !latestSession.signOutTime ? (
              <div className="d-flex flex-column">
                <span className="text-success mb-2">Signed In</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={
                    !availableActions.includes(WORK_LOG_ACTIONS.signOut)
                  }
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="d-flex flex-column">
                <span className="text-warning mb-2">Signed Out</span>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSignIn}
                  disabled={!availableActions.includes(WORK_LOG_ACTIONS.signIn)}
                >
                  Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showNonActiveMemberDialog && (
        <IncidentNonActiveConfirmDialog
          show={showNonActiveMemberDialog}
          onClose={() => setShowNonActiveMemberDialog(false)}
          active_time={positionMember.activeTime}
          user_guid={positionMember.userId}
          incident_member_id={positionMember.incidentMemberId}
        />
      )}

      <ConfirmDeleteDialog
        open={showDeleteConfirmDialog}
        title="Remove Member Confirmation"
        description={`Are you sure you want to remove ${positionMember.userName} from this position? This action cannot be undone.`}
        confirmLabel="Remove from Position"
        onOpenChange={(open) => {
          if (!open) setShowDeleteConfirmDialog(false);
        }}
        onConfirm={() => {
          removeMember.mutate({
            incidentMemberId: positionMember.incidentMemberId,
            positionId,
            isDraft,
            plannedPeriodId,
          });
          setShowDeleteConfirmDialog(false);
        }}
      />
    </div>
  );
};

export default PositionAssignee;
